import React, { useState, JSX } from "react";
import { DisciplinaryCaseType, CaseStatusType } from "../../misc/types";
import Pending from "../../assets/icons/Pending";
import CheckMark from "../../assets/icons/CheckMark";
import Cross from "../../assets/icons/Cross";
import Edit from "../../assets/icons/Edit";
import Delete from "../../assets/icons/Delete";
import ArrowDown from "../../assets/icons/ArrowDown";
import DisciplinaryDefaultCaseTable from "./DisciplinaryDefaultCaseTable";
import DefaultButton from "../default-button/DefaultButton";
import { Demerit, DisciplinaryCase } from "../../types";
import { accessGlobalMiscState, useGlobalMiscState } from "../../stores/misc";
import DisciplinaryTableItem from "../disciplinary-table-item/DisciplinaryTableItem";
import Modal from "../Modal/Modal";
import { showSnackbar, updateMiscState, userHasRole } from "../../misc/utils";
import { DisciplinaryDemeritView } from "./DisciplinaryDemeritView";
import { EditDisciplinaryCase } from "./EditDisciplinaryCase";
import Tick from "../../assets/icons/Tick";
import { simpleGetter } from "../../actions/common";
import { demeritsApi } from "../../api/api";
import { RejectDisciplinaryCase } from "./RejectDisciplinaryCase";
import UploadFile from "../../assets/icons/UploadFile";
import UploadDoc from "../../assets/icons/UploadDoc";
import FileWithTick from "../../assets/icons/FileWithTick";
import { HasRole } from "../HasRole";
import { FinaliseDisciplinaryCase } from "./FinaliseDisciplinaryCase";

const categoryList = [
  "Тип нарушения",
  "Студент",
  "Дата создания",
  "Статус",
  "",
];

type DisciplinaryTableCasesPropsType = {
  caseItem: DisciplinaryCase;
  hideCheckbox?: boolean;
  customButtonRender?: (c: any) => JSX.Element;
  type?: string;
};
const DisciplinaryTableCases = ({
  caseItem,
  hideCheckbox,
  customButtonRender,
  type = "cases",
}: DisciplinaryTableCasesPropsType) => {
  const miscState = useGlobalMiscState();
  const disciplinaryCasesSelectedForHearings =
    miscState.get("disciplinaryCasesSelectedForHearings") || [];
  const disciplinaryCasesDemeritViewModalOpen = Boolean(
    miscState.get("disciplinaryCasesDemeritViewModalOpen"),
  );
  const disciplinaryCasesEditCaseModalOpen = Boolean(
    miscState.get("disciplinaryCasesEditCaseModalOpen"),
  );
  const disciplinaryCasesRejectCaseModalOpen = Boolean(
    miscState.get("disciplinaryCasesRejectCaseModalOpen"),
  );
  const disciplinaryCasesFinaliseCaseModalOpen = Boolean(
    miscState.get("disciplinaryCasesFinaliseCaseModalOpen"),
  );
  const [isTableOpen, setIsTableOpen] = useState(false);

  const handleSetIsTableOpen = () => {
    setIsTableOpen((prev) => !prev);
  };

  const getStatus = (status: number) => {
    const statusMap: Record<number, any> = {
      0: {
        color: "text-gray-500",
        icon: <Pending />,
        label: "На рассмотрении",
      },
      1: {
        color: "text-green-500",
        icon: <CheckMark />,
        label: "Одобрено",
      },
      2: {
        color: "text-valley-main",
        icon: <FileWithTick />,
        label: "Завершено",
      },
      [-1]: { color: "text-red-500", icon: <Cross />, label: "Отказано" },
      [-2]: { color: "text-red-500", icon: <Cross />, label: "Отказано" },
    };

    return statusMap[status] || statusMap[0];
  };

  const { color, icon, label } = getStatus(caseItem.status);

  const onViewClick = (item: Demerit) => () => {
    accessGlobalMiscState().setBatch({
      disciplinaryCasesDemeritViewModalOpen: true,
      disciplinaryCasesDemeritCurrentCase: caseItem,
      disciplinaryCasesDemeritCurrentDemerit: item,
    });
  };

  const onEdit = () => {
    accessGlobalMiscState().setBatch({
      disciplinaryCasesEditCaseModalOpen: true,
      disciplinaryCasesDemeritCurrentCase: caseItem,
    });
  };

  const onDelete = async () => {
    const response = await simpleGetter(demeritsApi.updateDisciplinaryCase, {
      id: caseItem.id,
      is_archived: 1,
    });
    showSnackbar(
      response.success ? "Успешно" : "Произошла ошибка",
      response.success ? "success" : "error",
    );
    if (response.success) {
      accessGlobalMiscState().set(
        "disciplinaryCasesTriggerFetch",
        (accessGlobalMiscState().get("disciplinaryCasesTriggerFetch") || 0) + 1,
      );
    }
  };

  const updateDisciplinaryCaseStatus = (status: number) => async () => {
    if (![-1, 2].includes(status)) {
      const response = await simpleGetter(demeritsApi.updateDisciplinaryCase, {
        id: caseItem.id,
        status,
      });
      showSnackbar(
        response.success ? "Успешно" : "Произошла ошибка",
        response.success ? "success" : "error",
      );
      if (response.success) {
        accessGlobalMiscState().set(
          "disciplinaryCasesTriggerFetch",
          (accessGlobalMiscState().get("disciplinaryCasesTriggerFetch") || 0) +
            1,
        );
      }
      return;
    }
    accessGlobalMiscState().setBatch({
      disciplinaryCasesDemeritCurrentCase: caseItem,
      [status === 2
        ? "disciplinaryCasesFinaliseCaseModalOpen"
        : "disciplinaryCasesRejectCaseModalOpen"]: true,
    });
  };

  const onSelectCase = () => {
    if (disciplinaryCasesSelectedForHearings.includes(caseItem.id)) {
      const foundIndex = disciplinaryCasesSelectedForHearings.findIndex(
        (id: number) => id === caseItem.id,
      );
      miscState.setNoneFirst(
        "disciplinaryCasesSelectedForHearings",
        foundIndex,
      );
    } else {
      miscState.add("disciplinaryCasesSelectedForHearings", [caseItem.id]);
    }
  };

  const renderButtons = () => {
    let finalReturn = null;
    if (userHasRole("create_disciplinary_cases")) {
      finalReturn = (
        <React.Fragment>
          <DefaultButton
            Icon={Edit}
            buttonText='Редактировать'
            additionalStyle='min-w-[150px] px-3 py-1.5 border border-gray-300 text-gray-600 hover:bg-gray-100'
            onClick={onEdit}
          />
          <DefaultButton
            Icon={Delete}
            buttonText='Удалить'
            additionalStyle='min-w-[150px] px-3 py-1.5 border border-red-500 text-red-500 hover:bg-red-100'
            onClick={onDelete}
          />
        </React.Fragment>
      );
    }

    if (
      userHasRole("finalize_on_disciplinary_cases") &&
      type === "hearings_cases"
    ) {
      finalReturn = (
        <React.Fragment>
          <DefaultButton
            additionalStyle={"bg-valley-main text-white rounded-lg px-4 py-1.5"}
            buttonText='Завершить'
            Icon={FileWithTick}
            onClick={updateDisciplinaryCaseStatus(2)}
          />
        </React.Fragment>
      );
    } else if (
      userHasRole("decide_on_disciplinary_cases") &&
      type === "decide_cases"
    ) {
      finalReturn = (
        <React.Fragment>
          <DefaultButton
            additionalStyle={
              "bg-[#3BBC30] text-white w-1/2 border border-[#3BBC30] rounded-lg"
            }
            buttonText='Одобрить'
            Icon={Tick}
            onClick={updateDisciplinaryCaseStatus(1)}
          />
          <DefaultButton
            additionalStyle={
              "bg-[#F12D2D] text-white border border-[#F12D2D] w-1/2 rounded-lg"
            }
            buttonText='Отклонить'
            Icon={Cross}
            onClick={updateDisciplinaryCaseStatus(-1)}
          />
        </React.Fragment>
      );
    }

    return finalReturn;
  };

  return (
    <div className='p-5 rounded-lg bg-[#f8f9fd]'>
      <div className='grid grid-cols-[0.4fr_0.3fr_0.4fr_0.5fr] gap-4 items-center'>
        <p className='flex items-center gap-2 max-w-[90%] text-base leading-5'>
          {!hideCheckbox && (
            <input
              type='checkbox'
              checked={disciplinaryCasesSelectedForHearings.includes(
                caseItem.id,
              )}
              onChange={onSelectCase}
              // className='mr-2 h-5 w-5 border-gray-300 text-blue-500 focus:ring-0 bg-blue-100'
              className='h-5 w-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2'
            />
          )}
          <DefaultButton
            Icon={ArrowDown}
            onClick={handleSetIsTableOpen}
            additionalStyle={`px-1 ${
              isTableOpen
                ? "bg-blue-500 text-white"
                : "bg-transparent border border-gray-300 text-gray-500"
            }`}
            iconStyle={`transition-transform duration-300 ${
              isTableOpen ? "rotate-180" : "rotate-0"
            }`}
          />
          Дело №{caseItem.id}. {caseItem.name}
        </p>

        <p className={`flex items-center gap-1 ${color}`}>
          {type === "cases" && (
            <React.Fragment>
              {icon}
              {label}
            </React.Fragment>
          )}
        </p>

        <p className='text-gray-600'>
          Выговоров в деле:{" "}
          <span className='font-bold'>{caseItem.demerits.length}</span>
        </p>

        <div className='flex flex-1 justify-end gap-4'>
          {customButtonRender ? customButtonRender(caseItem) : renderButtons()}
        </div>
      </div>
      {caseItem.demerits.length > 0 && (
        <div
          className={`${
            isTableOpen
              ? "max-h-[500px] overflow-auto pt-4 pb-2 px-2"
              : "max-h-0 overflow-hidden"
          } transition-all duration-500 ease-in-out`}
        >
          <table className='w-full rounded-lg bg-white'>
            <thead>
              <tr>
                {categoryList.map((category, index) => (
                  <th
                    key={index}
                    className='px-4 py-2 border-b text-left text-sm font-medium text-valley-gray'
                  >
                    {category}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {caseItem.demerits.map((item, index) => (
                <DisciplinaryTableItem
                  onViewClick={onViewClick}
                  key={index}
                  item={item}
                  index={index}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal
        additionalClassName='w-[52%] h-[87.5%] rounded-xl flex flex-col !bg-transparent p-0'
        visible={
          disciplinaryCasesDemeritViewModalOpen &&
          miscState.get("disciplinaryCasesDemeritCurrentCase")?.id ===
            caseItem.id
        }
        setVisible={updateMiscState("disciplinaryCasesDemeritViewModalOpen")}
        renderClose={false}
      >
        <DisciplinaryDemeritView />
      </Modal>
      <Modal
        additionalClassName='!w-[60%] h-[98%] rounded-xl flex flex-col p-8'
        visible={
          disciplinaryCasesFinaliseCaseModalOpen &&
          miscState.get("disciplinaryCasesDemeritCurrentCase")?.id ===
            caseItem.id
        }
        setVisible={updateMiscState("disciplinaryCasesFinaliseCaseModalOpen")}
      >
        <FinaliseDisciplinaryCase />
      </Modal>
      <Modal
        additionalClassName='!w-[90%] h-[90%] rounded-xl flex flex-col p-8'
        visible={
          disciplinaryCasesEditCaseModalOpen &&
          miscState.get("disciplinaryCasesDemeritCurrentCase")?.id ===
            caseItem.id
        }
        setVisible={updateMiscState("disciplinaryCasesEditCaseModalOpen")}
      >
        <EditDisciplinaryCase />
      </Modal>
      <Modal
        additionalClassName='!w-[32.5%] rounded-xl flex flex-col p-8'
        visible={
          disciplinaryCasesRejectCaseModalOpen &&
          miscState.get("disciplinaryCasesDemeritCurrentCase")?.id ===
            caseItem.id
        }
        setVisible={updateMiscState("disciplinaryCasesRejectCaseModalOpen")}
      >
        <RejectDisciplinaryCase />
      </Modal>
    </div>
  );
};

export default DisciplinaryTableCases;
